import type { ChartBlockAbstract, ChartLikeProtocol } from '@lighthouse/core'
import { useBreakpoint } from '@lighthouse/tools'
import React, { memo, useMemo } from 'react'

import { useEffectiveAnchorId } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'
import { ExportContent } from './ExportContent'
import { IndicatorChartViewContent } from './IndicatorChartViewContent'
import { SingleChartViewContent } from './SingleChartViewContent'
import * as SC from './styles'

interface ChartBlockProps extends React.ComponentPropsWithoutRef<'div'> {
    loading?: boolean
    blockData: ChartBlockAbstract
    chartData?: ChartLikeProtocol
    onExport?: (ev: React.MouseEvent) => void
}

const ChartBlock: React.FC<ChartBlockProps> = ({ blockData, loading, chartData, onExport, ...rest }) => {
    const { id, title, config } = blockData
    const { ref, width } = useBreakpoint()
    const { chartType, pointer, canExport, breakPoint, anchor } = config
    const { anchorId } = anchor || {}
    const effectiveAnchorId = useEffectiveAnchorId(anchorId)
    useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)

    const Content = useMemo(() => {
        if (chartType === 'indicator') {
            return (
                <SC.ChildChartViewContainer>
                    <IndicatorChartViewContent title={title} loading={loading} chartOption={config} chartData={chartData} />
                </SC.ChildChartViewContainer>
            )
        }

        return (
            <SingleChartViewContent
                width={width}
                loading={loading}
                pointer={pointer}
                title={title}
                chartOption={config}
                chartData={chartData}
            />
        )
    }, [chartData, chartType, config, loading, pointer, title, width])

    return (
        <SC.ChartViewBlockContainer
            blockId={id}
            blockName={title}
            animation={breakPoint?.animation}
            id={effectiveAnchorId}
            // data-stop-action-propagation
            data-block-type="chartView"
            ref={ref}
            {...rest}
        >
            <SC.ChildContainer data-content-type="chartView">{Content}</SC.ChildContainer>
            {canExport && <ExportContent onExport={onExport} />}
        </SC.ChartViewBlockContainer>
    )
}

export default memo(ChartBlock)
